/**
 * SellerAvailabilitySettings — Embedded inside SellerTourCalendar as an
 * "Availability" tab. Sellers pick a property, set weekly windows, and
 * add/remove blocked dates.
 *
 * Props:
 *   sellerId  {number}  Required. The authenticated seller's user ID.
 *   initialHouseId {number|null} Optional. Pre-select this house on mount.
 */
import React, { useEffect, useState } from 'react';
import { useSellerAvailability } from '../../agents/tourRequestAgent';
import APIAgent from '../../agents/apiAgent';
import './SellerAvailabilitySettings.css';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

// ─── WeeklyWindowEditor ──────────────────────────────────────────────────────

function WeeklyWindowEditor({ windows, onChange }) {
  const windowByDow = Object.fromEntries(
    (windows || []).map((w) => [w.day_of_week, w])
  );

  const toggleDay = (dow) => {
    if (windowByDow[dow]) {
      onChange((windows || []).filter((w) => w.day_of_week !== dow));
    } else {
      onChange([...(windows || []), { day_of_week: dow, start_time: '09:00', end_time: '17:00' }]);
    }
  };

  const updateTime = (dow, field, value) => {
    onChange(
      (windows || []).map((w) =>
        w.day_of_week === dow ? { ...w, [field]: value } : w
      )
    );
  };

  return (
    <div className="sas-weekly-grid">
      {DAY_LABELS.map((label, dow) => {
        const win = windowByDow[dow];
        return (
          <div key={dow} className={`sas-day-row${win ? ' enabled' : ''}`}>
            <label className="sas-day-toggle">
              <input
                type="checkbox"
                checked={!!win}
                onChange={() => toggleDay(dow)}
              />
              <span className="sas-day-label">{label}</span>
            </label>
            <input
              type="time"
              className="sas-time-input"
              disabled={!win}
              value={win?.start_time ?? '09:00'}
              onChange={(e) => updateTime(dow, 'start_time', e.target.value)}
            />
            <span className="sas-time-sep">–</span>
            <input
              type="time"
              className="sas-time-input"
              disabled={!win}
              value={win?.end_time ?? '17:00'}
              onChange={(e) => updateTime(dow, 'end_time', e.target.value)}
            />
          </div>
        );
      })}
    </div>
  );
}

// ─── BlockedDatesList ─────────────────────────────────────────────────────────

function BlockedDatesList({ blockedDates, onAdd, onRemove, disabled }) {
  const [newDate, setNewDate] = useState('');
  const [newNote, setNewNote] = useState('');

  const handleAdd = () => {
    if (!newDate) return;
    onAdd(newDate, newNote);
    setNewDate('');
    setNewNote('');
  };

  return (
    <div className="sas-blocked">
      <h4 className="sas-blocked-title">Blocked Dates</h4>
      <ul className="sas-blocked-list">
        {(blockedDates || []).length === 0 && (
          <li className="sas-blocked-empty">No blocked dates</li>
        )}
        {(blockedDates || []).map((b) => (
          <li key={b.date} className="sas-blocked-item">
            <span className="sas-blocked-date">{b.date}</span>
            {b.note && <span className="sas-blocked-note">{b.note}</span>}
            <button
              className="sas-blocked-remove"
              onClick={() => onRemove(b.date)}
              disabled={disabled}
              aria-label={`Remove block for ${b.date}`}
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
      <div className="sas-blocked-add">
        <input
          type="date"
          className="sas-date-input"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
          disabled={disabled}
        />
        <input
          type="text"
          className="sas-note-input"
          placeholder="Note (optional)"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          disabled={disabled}
        />
        <button
          className="sas-add-btn"
          onClick={handleAdd}
          disabled={!newDate || disabled}
        >
          + Block Date
        </button>
      </div>
    </div>
  );
}

// ─── SellerAvailabilitySettings (root) ───────────────────────────────────────

export default function SellerAvailabilitySettings({ sellerId, initialHouseId }) {
  const [houses, setHouses] = useState([]);
  const [housesLoading, setHousesLoading] = useState(false);
  const [selectedHouseId, setSelectedHouseId] = useState(initialHouseId || null);

  // Load seller's houses on mount
  useEffect(() => {
    if (!sellerId) return;
    setHousesLoading(true);
    APIAgent.request(`/api/houses/seller/${sellerId}`, 'GET')
      .then((data) => {
        const raw = Array.isArray(data) ? data : data?.houses || [];
        const list = raw.filter((h) => h.listing_status === 'active');
        setHouses(list);
        if (!selectedHouseId && list.length > 0) {
          setSelectedHouseId(list[0].id);
        }
      })
      .catch((err) => console.error('Error loading seller houses:', err))
      .finally(() => setHousesLoading(false));
  }, [sellerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    availability,
    loading,
    error,
    fetchAvailability,
    upsertAvailability,
    addBlockedDate,
    removeBlockedDate,
  } = useSellerAvailability(selectedHouseId);

  const [windows, setWindows] = useState([]);
  const [duration, setDuration] = useState(30);
  const [saveError, setSaveError] = useState(null);
  const [saved, setSaved] = useState(false);

  // Fetch availability when selected house changes
  useEffect(() => {
    if (selectedHouseId) fetchAvailability();
  }, [selectedHouseId, fetchAvailability]);

  // Sync local state from fetched availability
  useEffect(() => {
    if (availability) {
      setWindows(availability.recurring_windows || []);
      setDuration(availability.default_duration_minutes || 30);
    } else {
      setWindows([]);
      setDuration(30);
    }
  }, [availability]);

  const handleSave = async () => {
    if (!selectedHouseId) return;
    setSaveError(null);
    setSaved(false);
    try {
      await upsertAvailability(
        {
          recurring_windows: windows,
          blocked_dates: availability?.blocked_dates ?? [],
          default_duration_minutes: duration,
        },
        sellerId
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setSaveError(err.message || 'Failed to save');
    }
  };

  if (!sellerId) return null;

  return (
    <div className="sas-root">
      <h3 className="sas-title">Tour Availability</h3>
      <p className="sas-subtitle">
        Set the days and times buyers can request tours for your listings.
      </p>

      {/* House selector */}
      {housesLoading && <div className="sas-loading">Loading your listings…</div>}
      {!housesLoading && houses.length === 0 && (
        <div className="sas-empty">No active listings found.</div>
      )}
      {!housesLoading && houses.length > 0 && (
        <div className="sas-house-selector">
          <label className="sas-label">Listing</label>
          <select
            className="sas-house-select"
            value={selectedHouseId || ''}
            onChange={(e) => setSelectedHouseId(Number(e.target.value))}
          >
            {houses.map((h) => (
              <option key={h.id} value={h.id}>
                {h.address}, {h.city}
                {!h.availability_configured ? ' (no availability set)' : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {selectedHouseId && (
        <>
          {error && <div className="sas-error">{error}</div>}

          {/* Not yet configured hint */}
          {!availability && !loading && (
            <div className="sas-unconfigured">
              ⚠️ No availability set for this property. Buyers can request any time.
              Define your windows below and save.
            </div>
          )}

          {/* Weekly windows */}
          <section className="sas-section">
            <h4 className="sas-section-title">Weekly Windows</h4>
            <WeeklyWindowEditor windows={windows} onChange={setWindows} />
            <div className="sas-duration-row">
              <label className="sas-label">Default tour duration</label>
              <select
                className="sas-duration-select"
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
              >
                {[15, 30, 45, 60].map((m) => (
                  <option key={m} value={m}>{m} min</option>
                ))}
              </select>
            </div>
            <div className="sas-save-row">
              <button
                className="sas-save-btn"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Saving…' : 'Save Availability'}
              </button>
              {saved && <span className="sas-saved-badge">✓ Saved</span>}
              {saveError && <span className="sas-save-error">{saveError}</span>}
            </div>
          </section>

          {/* Blocked dates — only show after availability configured */}
          <section className="sas-section">
            <BlockedDatesList
              blockedDates={availability?.blocked_dates ?? []}
              onAdd={addBlockedDate}
              onRemove={removeBlockedDate}
              disabled={!availability || loading}
            />
            {!availability && (
              <p className="sas-blocked-hint">Save your weekly windows first to add blocked dates.</p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
